import { ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Tag, Tooltip, Typography, theme } from 'antd';
import type { CSSProperties, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { attendeeTypeLabelKey } from '../i18n/attendeeType';

const { Text } = Typography;

const ROOT_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  minWidth: 0,
  contain: 'inline-size',
};

// As tall as the name line, so the icon centres on it and not on both lines.
const ICON_STYLE: CSSProperties = { color: '#8c8c8c', fontSize: 12, height: 22, flexShrink: 0 };

const TAG_STYLE: CSSProperties = { fontSize: 11, padding: '0 6px', margin: 0, flexShrink: 0 };

// A long name without spaces to break at would otherwise wrap letter by letter
// or push the tags off the line; it is cut instead, and the tags never shrink.
const NAME_STYLE: CSSProperties = {
  fontSize: 13,
  minWidth: 0,
  maxWidth: 200,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export interface TicketOwner {
  firstName?: string;
  lastName?: string;
  email?: string;
  attendeeType?: string;
  User?: { firstName?: string; lastName?: string; email: string };
}

interface TicketOwnerCellProps {
  ticket: TicketOwner;
}

// Name and e-mail share one column beside the icon, so their left edges match.
export function TicketOwnerCell({ ticket }: TicketOwnerCellProps): JSX.Element {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const firstName = ticket.User?.firstName || ticket.firstName || '';
  const lastName = ticket.User?.lastName || ticket.lastName || '';
  const email = ticket.User?.email || ticket.email || '';
  const fullName = `${firstName} ${lastName}`.trim();
  // Same condition as the assignment-status filter, so the list and the filter agree.
  const unassigned = !ticket.email;
  // An unknown category shows nothing: a raw enum value tells the organizer less.
  const categoryKey = attendeeTypeLabelKey(ticket.attendeeType ?? '');

  return (
    // `contain: inline-size`: the unwrapped name must not widen the column,
    // only fill the width the table gives it.
    <div style={ROOT_STYLE}>
      <UserOutlined style={ICON_STYLE} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          data-owner-line="name"
          style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
        >
          <Text strong style={NAME_STYLE} title={fullName || email || undefined}>
            {fullName || email || '—'}
          </Text>
          {categoryKey && (
            <Tag bordered={false} color="blue" style={TAG_STYLE}>
              {t(categoryKey)}
            </Tag>
          )}
          {/* An icon, not a tag, so the name keeps the room. */}
          {unassigned && (
            <Tooltip title={t('tickets.tagUnassigned')}>
              <ClockCircleOutlined
                role="img"
                aria-label={t('tickets.tagUnassigned')}
                style={{ color: token.colorWarning, fontSize: 14, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </div>
        {fullName && email && (
          <div
            data-owner-line="email"
            style={{
              fontSize: 12,
              color: '#8c8c8c',
              marginTop: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 220,
            }}
            title={email}
          >
            {email}
          </div>
        )}
      </div>
    </div>
  );
}

export default TicketOwnerCell;
